import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { message, DatePicker } from "antd";
import dayjs from "dayjs";
import ReactECharts from "echarts-for-react";
import "../css/main.scss";
import Header from "./common/Header";
import SiderBar from "./common/SiderBar";
import { get_statistic } from "../apis/GetStatistics";
import { get_currency_stats } from "../apis/GetCurrencyStats";
import { get_daily_credited_amount } from "../apis/GetDailyCreditedAmount";
import Loading from "./common/Loading";

const { RangePicker } = DatePicker;

const Main = () => {
  const navigate = useNavigate();

  // 统计数据状态
  const [statistics, setStatistics] = useState({
    totalUsers: 0,
    paymentAccounts: 0,
    transactionRecords: 0,
    unreadMessages: 0,
    currentRole: "加载中...",
  });

  // 币种统计数据
  const [currencyStats, setCurrencyStats] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);

  // 每日入账金额统计数据
  const [dailyStats, setDailyStats] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [lineChartLoading, setLineChartLoading] = useState(true);

  // 时间范围状态（默认最近30天）
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(29, "day"), // 30天前（包含今天共30天）
    dayjs(), // 今天
  ]);

  // 获取统计数据
  const fetchStatistics = async (startDate, endDate) => {
    try {
      setLoading(true);
      const response = await get_statistic(startDate, endDate);

      if (response.data && response.data.code === 200) {
        const data = response.data.data;
        console.log(data);

        setStatistics({
          totalUsers: data.user_count || 0,
          paymentAccounts: data.account_count || 0,
          transactionRecords: data.transaction_count || 0,
          unreadMessages: data.message_count || 0,
          currentRole: data.role || "管理员",
        });

        console.log(statistics);
      } else {
        message.error(response.data?.message || "获取统计数据失败");
      }
    } catch (err) {
      console.error("获取统计数据失败:", err);
      message.error("获取统计数据失败，请刷新页面重试");
    } finally {
      setLoading(false);
    }
  };

  // 获取币种统计数据
  const fetchCurrencyStats = async (startDate, endDate) => {
    try {
      setChartLoading(true);
      const response = await get_currency_stats(startDate, endDate);

      if (response.data && response.data.code === 200) {
        const data = response.data.data;
        setCurrencyStats(data.currency_stats || []);
        setTotalAmount(data.total_all || 0);
      } else {
        message.error(response.data?.message || "获取币种统计失败");
      }
    } catch (err) {
      console.error("获取币种统计失败:", err);
      message.error("获取币种统计失败");
    } finally {
      setChartLoading(false);
    }
  };

  // 获取每日入账金额统计数据
  const fetchDailyStats = async (startDate, endDate) => {
    try {
      setLineChartLoading(true);
      const params = {};
      if (startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else {
        params.days = 30;
      }
      const response = await get_daily_credited_amount(params);

      if (response.data && response.data.code === 200) {
        const data = response.data.data;
        setDailyStats(data.daily_stats || []);
        setCurrencies(data.currencies || []);
      } else {
        message.error(response.data?.message || "获取每日入账统计失败");
      }
    } catch (err) {
      console.error("获取每日入账统计失败:", err);
      message.error("获取每日入账统计失败");
    } finally {
      setLineChartLoading(false);
    }
  };

  // 时间范围变化处理
  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
      const startDate = dates[0].format("YYYY-MM-DD");
      const endDate = dates[1].format("YYYY-MM-DD");

      // 重新获取所有数据
      fetchStatistics(startDate, endDate);
      fetchCurrencyStats(startDate, endDate);
      fetchDailyStats(startDate, endDate);
    } else {
      // 如果清空时间选择，恢复默认（最近30天）
      const defaultStart = dayjs().subtract(29, "day");
      const defaultEnd = dayjs();
      setDateRange([defaultStart, defaultEnd]);

      fetchStatistics(null, null);
      fetchCurrencyStats(null, null);
      fetchDailyStats(null, null);
    }
  };

  // 初始化加载数据
  useEffect(() => {
    const startDate = dateRange[0]?.format("YYYY-MM-DD");
    const endDate = dateRange[1]?.format("YYYY-MM-DD");
    fetchStatistics(startDate, endDate);
    fetchCurrencyStats(startDate, endDate);
    fetchDailyStats(startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 快捷操作
  const quickActions = [
    { icon: "/home/m1-1.png", label: "用户管理", link: "/user-management" },
    { icon: "/home/m1-2.png", label: "添加用户", link: "/add-edit-user" },
    { icon: "/home/m1-3.png", label: "入账订单", link: "/incoming-orders" },
    { icon: "/home/m1-4.png", label: "账户管理", link: "/account-management" },
  ];

  // 折线图配置
  const getLineChartOption = () => {
    if (
      !dailyStats ||
      dailyStats.length === 0 ||
      !currencies ||
      currencies.length === 0
    ) {
      return {
        title: {
          text: "暂无数据",
          left: "center",
          top: "center",
          textStyle: {
            color: "#999",
            fontSize: 16,
          },
        },
      };
    }

    // 定义颜色数组，为每种币种分配不同颜色
    const colors = [
      "#6366f1", // indigo
      "#8b5cf6", // purple
      "#ec4899", // pink
      "#f59e0b", // amber
      "#10b981", // emerald
      "#3b82f6", // blue
      "#ef4444", // red
      "#14b8a6", // teal
      "#f97316", // orange
      "#84cc16", // lime
    ];

    // 准备折线图数据
    const dates = dailyStats.map((item) => {
      // 格式化日期，只显示月-日
      const date = new Date(item.date);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    });

    // 为每种币种创建一条线
    const series = currencies.map((currency, index) => {
      const amounts = dailyStats.map((item) => item[currency] || 0);
      const color = colors[index % colors.length];

      return {
        name: currency,
        type: "line",
        smooth: false, // 使用直线连接，不使用平滑曲线
        symbol: "circle",
        symbolSize: 6,
        lineStyle: {
          width: 2,
          color: color,
        },
        itemStyle: {
          color: color,
        },
        emphasis: {
          focus: "series",
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: color,
          },
        },
        data: amounts,
      };
    });

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
          label: {
            backgroundColor: "#6a7985",
          },
        },
      },
      legend: {
        data: currencies,
        top: 10,
        textStyle: {
          color: "#17233d",
          fontSize: 12,
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        top: "15%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: dates,
        axisLabel: {
          color: "#666",
          fontSize: 12,
          rotate: 45, // 如果日期太多，可以旋转45度
        },
        axisLine: {
          lineStyle: {
            color: "#e0e0e0",
          },
        },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          color: "#666",
          fontSize: 12,
          formatter: (value) => {
            if (value >= 1000000) {
              return (value / 1000000).toFixed(1) + "M";
            } else if (value >= 1000) {
              return (value / 1000).toFixed(1) + "K";
            }
            return value.toFixed(0);
          },
        },
        axisLine: {
          lineStyle: {
            color: "#e0e0e0",
          },
        },
        splitLine: {
          lineStyle: {
            color: "#f0f0f0",
            type: "dashed",
          },
        },
      },
      series: series,
    };
  };

  // 饼图配置
  const getPieChartOption = () => {
    if (!currencyStats || currencyStats.length === 0) {
      return {
        title: {
          text: "暂无数据",
          left: "center",
          top: "center",
          textStyle: {
            color: "#999",
            fontSize: 16,
          },
        },
      };
    }

    // 定义颜色数组
    const colors = [
      "#6366f1",
      "#8b5cf6",
      "#ec4899",
      "#f59e0b",
      "#10b981",
      "#3b82f6",
      "#ef4444",
      "#14b8a6",
    ];

    // 准备饼图数据
    const pieData = currencyStats.map((item, index) => ({
      value: item.total_amount,
      name: item.currency,
      itemStyle: {
        color: colors[index % colors.length],
      },
    }));

    return {
      tooltip: {
        trigger: "item",
        formatter: (params) => {
          const percent = params.percent;
          const value = params.value.toLocaleString("zh-CN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          return `${params.name}<br/>${value}<br/>占比: ${percent}%`;
        },
      },
      legend: {
        orient: "vertical",
        left: "right",
        top: "center",
        textStyle: {
          color: "#17233d",
          fontSize: 14,
        },
        formatter: (name) => {
          const item = currencyStats.find((s) => s.currency === name);
          if (item) {
            const amount = item.total_amount.toLocaleString("zh-CN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
            return `${name}: ${amount}`;
          }
          return name;
        },
      },
      series: [
        {
          name: "金额分布",
          type: "pie",
          radius: ["40%", "70%"],
          center: ["35%", "50%"],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 8,
            borderColor: "#fff",
            borderWidth: 2,
          },
          label: {
            show: true,
            formatter: (params) => {
              return `${params.name}\n${params.percent}%`;
            },
            fontSize: 12,
            color: "#17233d",
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 14,
              fontWeight: "bold",
            },
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
          data: pieData,
        },
      ],
    };
  };

  return (
    <div className="main-container">
      <Header />
      <div className="main-content-wrapper">
        <SiderBar />

        <main className="main-content">
          {loading ? (
            <Loading />
          ) : (
            <div className="admin-dashboard">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <h1 className="dashboard-title">管理员控制台</h1>
                <RangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  format="YYYY-MM-DD"
                  placeholder={["开始日期", "结束日期"]}
                  style={{ marginLeft: "auto" }}
                />
              </div>

              {/* 数据统计卡片 */}
              <section className="statistics-cards">
                <div className="stat-card stat-card-users">
                  <h2 className="stat-number">{statistics.totalUsers}</h2>
                  <p className="stat-label">用户总数</p>
                </div>

                <div className="stat-card stat-card-accounts">
                  <h2 className="stat-number">{statistics.paymentAccounts}</h2>
                  <p className="stat-label">收款账户</p>
                </div>

                <div className="stat-card stat-card-transactions">
                  <h2 className="stat-number">
                    {statistics.transactionRecords}
                  </h2>
                  <p className="stat-label">交易记录</p>
                </div>

                <div className="stat-card stat-card-messages">
                  <h2 className="stat-number">{statistics.unreadMessages}</h2>
                  <p className="stat-label">未读消息</p>
                </div>

                <div className="stat-card stat-card-role">
                  <h2 className="stat-role-text">{statistics.currentRole}</h2>
                  <p className="stat-label">当前角色</p>
                </div>
              </section>

              {/* 图表区域：饼图和折线图并排 */}
              <section className="charts-section">
                <div className="charts-container">
                  {/* 金额分布饼图 */}
                  <div className="chart-item">
                    <h2 className="section-title">金额分布（按币种）</h2>
                    <div className="chart-wrapper">
                      {chartLoading ? (
                        <div className="chart-loading">
                          <Loading />
                        </div>
                      ) : (
                        <ReactECharts
                          option={getPieChartOption()}
                          style={{ height: "400px", width: "100%" }}
                          opts={{ renderer: "canvas" }}
                        />
                      )}
                    </div>
                  </div>

                  {/* 每日入账金额折线图 */}
                  <div className="chart-item">
                    <h2 className="section-title">
                      每日入账金额趋势
                      {dateRange && dateRange.length === 2 && (
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: "normal",
                            color: "#666",
                            marginLeft: "10px",
                          }}
                        >
                          ({dateRange[0].format("YYYY-MM-DD")} 至{" "}
                          {dateRange[1].format("YYYY-MM-DD")})
                        </span>
                      )}
                    </h2>
                    <div className="chart-wrapper">
                      {lineChartLoading ? (
                        <div className="chart-loading">
                          <Loading />
                        </div>
                      ) : (
                        <ReactECharts
                          option={getLineChartOption()}
                          style={{ height: "400px", width: "100%" }}
                          opts={{ renderer: "canvas" }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </section>

              {/* 快捷操作 */}
              <section className="quick-nav">
                <h2 className="section-title">快捷操作</h2>
                <div className="quick-nav-grid">
                  {quickActions.map((item, index) => (
                    <div
                      key={index}
                      className="quick-nav-item"
                      onClick={() => navigate(item.link)}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="nav-icon">
                        <img alt="" src={item.icon} />
                      </div>
                      <span>{item.label}</span>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}
        </main>
      </div>
      <footer className="page-footer">
        <p>© 2022 GLOBAL E-PAYMENT LIMITED. All Rights Reserved.</p>
      </footer>
    </div>
  );
};

export default Main;
